import { useState } from "react";
import { api } from "../api";
import { ArrowRight, CheckCircle2, Users, Briefcase } from "lucide-react";
import SEO from "../components/SEO";

function ApplicationPage({ role, title, subtitle, kicker, fields, cta, heroImg }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", ...Object.fromEntries(fields.map(f => [f.k, f.default ?? ""])) });
  const [status, setStatus] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const payload = { role, ...form };
      ["client_portfolio_pmi", "years_experience", "availability_months"].forEach(k => {
        if (payload[k] !== undefined) payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      await api.post("/platform/applications", payload);
      setStatus("sent");
    } catch { setStatus("error"); }
  };
  return (
    <main data-testid={`${role}-page`}>
      <SEO
        title={role === "partner" ? "Rete Partner Pro — Commercialisti & Advisor" : "Academy Direttori Generali"}
        description={role === "partner"
          ? "La rete distribuita di commercialisti e advisor GIANO. Kit di pre-qualifica, dashboard referral con success fee trasparente, certificazione Giano Partner."
          : "Formiamo manager 35-45 anni come Direttori Generali delle nostre partecipate. Con equity virtuale. Per guidare una PMI italiana dopo il fondatore."}
        path={role === "partner" ? "/partner" : "/academy"}
      />
      <section style={{ background: role === "partner" ? "var(--bone)" : "var(--midnight)", color: role === "partner" ? "inherit" : "var(--bone)" }} className="py-20 md:py-28">
        <div className="container-x">
          <div className="overline" style={{ color: role === "partner" ? "var(--terracotta)" : "var(--gold)" }}>{kicker}</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 1000 }}>{title}</h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 780, opacity: 0.82, lineHeight: 1.7 }}>{subtitle}</p>
        </div>
      </section>
      <section className="py-16 md:py-20" style={{ background: "var(--muted)" }}>
        <div className="container-x max-w-3xl">
          {status === "sent" ? (
            <div className="card-flat p-10 text-center" data-testid={`${role}-success`}>
              <CheckCircle2 size={40} style={{ color: "#065F46", margin: "0 auto" }} />
              <h3 className="serif-display mt-6" style={{ fontSize: 32 }}>Candidatura ricevuta</h3>
              <p className="mt-4" style={{ opacity: 0.75 }}>Il nostro team la esamina entro 5 giorni lavorativi e ti contatta all'indirizzo indicato.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="card-flat p-8 md:p-10" data-testid={`${role}-form`}>
              <h3 className="serif-display" style={{ fontSize: 26 }}>{cta}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
                <F label="Nome e cognome *"><input required data-testid={`${role}-name`} className="input-line" value={form.full_name} onChange={e => set("full_name", e.target.value)} /></F>
                <F label="Email *"><input required type="email" data-testid={`${role}-email`} className="input-line" value={form.email} onChange={e => set("email", e.target.value)} /></F>
                <F label="Telefono"><input data-testid={`${role}-phone`} className="input-line" value={form.phone} onChange={e => set("phone", e.target.value)} /></F>
                {fields.map(f => (
                  <F key={f.k} label={f.label} span={f.span}>
                    {f.type === "textarea"
                      ? <textarea data-testid={`${role}-${f.k}`} rows={3} className="input-line" style={{ resize: "vertical" }} value={form[f.k]} onChange={e => set(f.k, e.target.value)} />
                      : f.type === "select"
                        ? <select data-testid={`${role}-${f.k}`} className="input-line" style={{ background: "transparent" }} value={form[f.k]} onChange={e => set(f.k, e.target.value)}><option value="">—</option>{f.options.map(o => <option key={o}>{o}</option>)}</select>
                        : f.type === "checkbox"
                          ? <label className="flex gap-3 text-sm mt-1"><input type="checkbox" data-testid={`${role}-${f.k}`} checked={!!form[f.k]} onChange={e => set(f.k, e.target.checked)} /><span>{f.hint}</span></label>
                          : <input type={f.type || "text"} data-testid={`${role}-${f.k}`} className="input-line" value={form[f.k]} onChange={e => set(f.k, e.target.value)} />}
                  </F>
                ))}
              </div>
              <div className="mt-8">
                <button type="submit" disabled={status === "loading"} className="btn-primary" data-testid={`${role}-submit`}>
                  {status === "loading" ? "..." : "Invia candidatura"} <ArrowRight size={16} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
function F({ label, children }) { return <div><label className="overline block mb-1">{label}</label>{children}</div>; }

export function Partner() {
  return <ApplicationPage
    role="partner"
    kicker="Rete Partner Pro · Commercialisti e Advisor"
    title={<>Trasforma un bilancio fiscale<br /><span style={{ color: "var(--terracotta)", fontStyle: "italic" }}>in un dossier vendibile</span> in 48 ore.</>}
    subtitle="Kit di pre-qualifica GIANO, dashboard referral, success fee trasparente, certificazione 'Giano Partner'. La rete commerciale distribuita del mercato PMI italiano."
    cta="Candida il tuo studio"
    fields={[
      { k: "firm_name", label: "Nome studio" },
      { k: "city", label: "Città" },
      { k: "firm_size", label: "Dimensione studio", type: "select", options: ["Solo", "2-5 persone", "6-15 persone", "16-40 persone", "40+ persone"] },
      { k: "client_portfolio_pmi", label: "Clienti PMI (n° stimato)", type: "number" },
      { k: "aida_licence", label: "Licenza AIDA/Cerved", type: "checkbox", hint: "Ho già una licenza AIDA o Cerved attiva nel mio studio." },
      { k: "motivation", label: "Perché ti interessa", type: "textarea", span: 2 },
    ]}
  />;
}

export function Academy() {
  return <ApplicationPage
    role="academy"
    kicker="Academy dei Direttori Generali"
    title={<>Guidare un'azienda<br /><span style={{ color: "var(--gold)", fontStyle: "italic" }}>dopo il fondatore.</span></>}
    subtitle="Il vero collo di bottiglia dei serial acquirer è chi guida l'azienda dopo il passaggio generazionale. Cerchiamo manager 35-45 anni per formarli come DG delle nostre partecipate — con equity virtuale. Talenti MBA benvenuti."
    cta="Candida la tua candidatura"
    fields={[
      { k: "current_role", label: "Ruolo attuale" },
      { k: "years_experience", label: "Anni di esperienza manageriale", type: "number" },
      { k: "industries", label: "Settori di esperienza" },
      { k: "availability_months", label: "Disponibilità (mesi da oggi)", type: "number" },
      { k: "linkedin_url", label: "LinkedIn URL" },
      { k: "motivation", label: "Perché vuoi guidare una PMI italiana", type: "textarea", span: 2 },
    ]}
  />;
}

// Default export: Partner (route /partner)
export default Partner;
